/**
 * AI Command Center — unified hub for all AI-powered features.
 * Tabs: Feed | Workflows | Discipline | Education | Settings
 */
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../services/api";
import AIBadge from "../common/AIBadge";
import AITextHelper from "../common/AITextHelper";
import grids from "../responsive-fixes/grids.module.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface AINotification {
  id?: string;
  event_type: string;
  title: string;
  message: string;
  urgency: "low" | "medium" | "high" | "critical";
  action?: string;
  generated_at?: string;
  source?: string;
  [key: string]: any;
}

interface Workflow {
  id: string;
  type: string;
  context: Record<string, any>;
  recommendation: Record<string, any>;
  status: "pending" | "approved" | "rejected";
  created_at: string;
}

interface AISettings {
  providers: {
    openai: { configured: boolean; key_preview: string | null; model: string };
    ollama: { url: string; model: string };
  };
  features: { smart_notifications: boolean; daily_workflows: boolean; discipline_alerts: boolean };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const URGENCY_COLOR: Record<string, string> = {
  low: "#22c55e",
  medium: "#f59e0b",
  high: "#f97316",
  critical: "#ef4444",
};

const URGENCY_BG: Record<string, string> = {
  low: "rgba(34,197,94,0.12)",
  medium: "rgba(245,158,11,0.12)",
  high: "rgba(249,115,22,0.12)",
  critical: "rgba(239,68,68,0.12)",
};

const TABS = [
  { key: "feed",       label: "AI Feed",       icon: "📡" },
  { key: "workflows",  label: "Workflows",      icon: "⚙️" },
  { key: "discipline", label: "Discipline",     icon: "⚖️" },
  { key: "education",  label: "Education Hub",  icon: "📚" },
  { key: "settings",   label: "Settings",       icon: "🔧" },
];

const MODULE_OPTIONS = ["crm","whatsapp","inventory","pricing","attendance","accounting","marketplace","seo"];
const EMPLOYEE_OPTIONS = ["Hafiz","Nadir","Mehriban","Abdolmadjid Masoomi"];
const ISSUE_TYPES = ["Attendance","Performance","Conduct","Insubordination","Policy Violation"];

// ── Subcomponents ─────────────────────────────────────────────────────────────

function UrgencyBadge({ urgency }: { urgency: string }) {
  return (
    <span style={{
      padding: "0.15rem 0.5rem",
      borderRadius: 10,
      fontSize: "0.7rem",
      fontWeight: 700,
      background: URGENCY_BG[urgency] || URGENCY_BG.medium,
      color: URGENCY_COLOR[urgency] || URGENCY_COLOR.medium,
      border: `1px solid ${URGENCY_COLOR[urgency] || URGENCY_COLOR.medium}44`,
      textTransform: "uppercase" as const,
      letterSpacing: "0.05em",
    }}>
      {urgency}
    </span>
  );
}

function Thinking() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#ffd6a5", padding: "1rem" }}>
      <span style={{ animation: "pulse 1.5s infinite" }}>✨</span>
      <span style={{ fontSize: "0.9rem" }}>Thinking…</span>
    </div>
  );
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} style={btnGhost} title="Copy to clipboard">
      {copied ? "✅ Copied" : "📋 Copy"}
    </button>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(200,191,255,0.15)",
  borderRadius: 10,
  padding: "1rem",
  marginBottom: "1rem",
};

const btnPrimary: React.CSSProperties = {
  padding: "0.45rem 1rem",
  background: "rgba(255,214,165,0.2)",
  border: "1px solid rgba(255,214,165,0.4)",
  borderRadius: 6,
  color: "#ffd6a5",
  cursor: "pointer",
  fontSize: "0.85rem",
  fontWeight: 600,
};

const btnGhost: React.CSSProperties = {
  padding: "0.35rem 0.75rem",
  background: "transparent",
  border: "1px solid rgba(156,163,175,0.3)",
  borderRadius: 6,
  color: "var(--text-muted, #9ca3af)",
  cursor: "pointer",
  fontSize: "0.8rem",
};

const btnDanger: React.CSSProperties = {
  ...btnGhost,
  borderColor: "rgba(239,68,68,0.4)",
  color: "#ef4444",
};

const selectStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 6,
  color: "#e2e8f0",
  padding: "0.4rem 0.6rem",
  fontSize: "0.85rem",
  width: "100%",
};

const inputStyle: React.CSSProperties = {
  ...selectStyle,
};

const label: React.CSSProperties = {
  fontSize: "0.78rem",
  color: "var(--text-muted, #9ca3af)",
  marginBottom: 4,
  display: "block",
};

const sectionTitle: React.CSSProperties = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "#e2e8f0",
  marginBottom: "0.75rem",
};

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — AI FEED
// ══════════════════════════════════════════════════════════════════════════════

function TabFeed() {
  const [notifications, setNotifications] = useState<AINotification[]>([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    (api as any).aiCenter.notifications().then((data: AINotification[]) => {
      setNotifications(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const EVENT_MODULE: Record<string, string> = {
    low_stock: "inventory",
    lead_followup: "crm",
    kpi_alert: "employee",
    price_opportunity: "pricing",
    unanswered_whatsapp: "whatsapp",
  };

  const MODULE_ICONS: Record<string, string> = {
    inventory: "📦", crm: "🎯", employee: "👤",
    pricing: "💰", whatsapp: "💬", all: "📡",
  };

  const filtered = notifications.filter(n => {
    if (dismissed.has(n.id || "")) return false;
    if (filter === "all") return true;
    return EVENT_MODULE[n.event_type] === filter;
  });

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1.5rem", flexWrap: "wrap" }}>
        {["all","crm","inventory","employee","pricing","whatsapp"].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              ...btnGhost,
              borderColor: filter === f ? "rgba(255,214,165,0.5)" : undefined,
              color: filter === f ? "#ffd6a5" : undefined,
            }}
          >
            {MODULE_ICONS[f]} {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading && <Thinking />}
      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "3rem" }}>
          <p style={{ fontSize: "2rem" }}>📭</p>
          <p>No AI notifications yet</p>
        </div>
      )}

      {filtered.map((n, i) => (
        <div key={n.id || i} style={{ ...card, borderLeft: `3px solid ${URGENCY_COLOR[n.urgency] || "#6b7280"}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: "1rem" }}>{MODULE_ICONS[EVENT_MODULE[n.event_type]] || "🤖"}</span>
                <strong style={{ fontSize: "0.95rem" }}>{n.title}</strong>
                <UrgencyBadge urgency={n.urgency} />
                <AIBadge />
              </div>
              <p style={{ margin: "0 0 0.5rem", fontSize: "0.875rem", color: "#cbd5e1" }}>{n.message}</p>
              {n.action && (
                <p style={{ margin: 0, fontSize: "0.8rem", color: "#ffd6a5" }}>
                  → {n.action}
                </p>
              )}
              {n.whatsapp_opener && (
                <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "#94a3b8", fontStyle: "italic" }}>
                  💬 "{n.whatsapp_opener}"
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button style={btnPrimary}>Apply</button>
              <button style={btnGhost} onClick={() => setDismissed(prev => new Set(Array.from(prev).concat(n.id || "")))}>
                Dismiss
              </button>
            </div>
          </div>
          {n.generated_at && (
            <p style={{ margin: "0.5rem 0 0", fontSize: "0.72rem", color: "#475569" }}>
              Generated {new Date(n.generated_at).toLocaleString("en-AE")} · via {n.source || "ai"}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — WORKFLOWS
// ══════════════════════════════════════════════════════════════════════════════

function TabWorkflows() {
  const [pending, setPending] = useState<Workflow[]>([]);
  const [history, setHistory] = useState<Workflow[]>([]);
  const [view, setView] = useState<"pending" | "history">("pending");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      (api as any).aiCenter.workflowPending(),
      (api as any).aiCenter.workflowHistory(),
    ]).then(([p, h]: [Workflow[], Workflow[]]) => {
      setPending(p);
      setHistory(h);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id: string) => {
    await (api as any).aiCenter.approveWorkflow(id);
    load();
  };

  const reject = async (id: string) => {
    const reason = prompt("Rejection reason:");
    if (!reason) return;
    await (api as any).aiCenter.rejectWorkflow(id, reason);
    load();
  };

  const WF_ICONS: Record<string, string> = {
    LEAD_NURTURE: "🎯",
    INVENTORY_REORDER: "📦",
    EMPLOYEE_COACHING: "👤",
    PRICE_OPTIMIZATION: "💰",
    CUSTOMER_WINBACK: "💌",
  };

  const items = view === "pending" ? pending : history;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: "1.5rem" }}>
        {(["pending", "history"] as const).map(v => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{ ...btnGhost, borderColor: view === v ? "rgba(255,214,165,0.5)" : undefined,
                     color: view === v ? "#ffd6a5" : undefined }}
          >
            {v === "pending" ? `⏳ Pending (${pending.length})` : "📋 History"}
          </button>
        ))}
        <button style={{ ...btnGhost, marginLeft: "auto" }} onClick={load}>↻ Refresh</button>
      </div>

      {loading && <Thinking />}
      {!loading && items.length === 0 && (
        <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "3rem" }}>
          No {view} workflows.
        </div>
      )}

      {items.map((wf) => (
        <div key={wf.id} style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span>{WF_ICONS[wf.type] || "⚙️"}</span>
                <strong>{wf.type.replace(/_/g, " ")}</strong>
                <span style={{
                  padding: "0.1rem 0.4rem", borderRadius: 8, fontSize: "0.7rem",
                  fontWeight: 700,
                  background: wf.status === "approved" ? "rgba(34,197,94,0.15)" :
                               wf.status === "rejected" ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)",
                  color: wf.status === "approved" ? "#22c55e" :
                         wf.status === "rejected" ? "#ef4444" : "#f59e0b",
                }}>
                  {wf.status}
                </span>
                <AIBadge />
              </div>
              <div style={{ fontSize: "0.85rem", color: "#94a3b8", marginBottom: 6 }}>
                {new Date(wf.created_at).toLocaleString("en-AE")}
              </div>
              <pre style={{
                background: "rgba(0,0,0,0.2)", borderRadius: 6, padding: "0.5rem",
                fontSize: "0.78rem", color: "#cbd5e1", whiteSpace: "pre-wrap", margin: 0,
              }}>
                {JSON.stringify(wf.recommendation, null, 2)}
              </pre>
            </div>
            {wf.status === "pending" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginLeft: 12, flexShrink: 0 }}>
                <button style={btnPrimary} onClick={() => approve(wf.id)}>✅ Approve</button>
                <button style={btnDanger} onClick={() => reject(wf.id)}>✗ Reject</button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 3 — DISCIPLINE CONSOLE
// ══════════════════════════════════════════════════════════════════════════════

function TabDiscipline() {
  const [employee, setEmployee] = useState(EMPLOYEE_OPTIONS[0]);
  const [role, setRole] = useState("Employee");
  const [issueType, setIssueType] = useState(ISSUE_TYPES[0]);
  const [details, setDetails] = useState("");
  const [warnCount, setWarnCount] = useState(0);
  const [writtenCount, setWrittenCount] = useState(0);
  const [consultation, setConsultation] = useState<any>(null);
  const [letter, setLetter] = useState<string | null>(null);
  const [letterLang, setLetterLang] = useState<"english" | "arabic">("english");
  const [pip, setPip] = useState<any>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [incidentDate, setIncidentDate] = useState(new Date().toISOString().slice(0, 10));

  const consult = async () => {
    setLoading("consult");
    setConsultation(null);
    try {
      const res = await (api as any).aiCenter.disciplineConsult({
        name: employee, role, months_tenure: 12,
        issue_type: issueType, details,
        warning_count: warnCount, written_count: writtenCount,
      });
      setConsultation(res);
    } finally { setLoading(null); }
  };

  const genLetter = async () => {
    if (!consultation) return;
    setLoading("letter");
    setLetter(null);
    try {
      const res = await (api as any).aiCenter.warningLetter({
        name: employee, role, issue: details, incident_date: incidentDate,
        level: consultation.recommendation === "verbal_warning" ? "Verbal" :
               consultation.recommendation === "written_warning" ? "First Written" : "Final Written",
        history: `${warnCount} verbal, ${writtenCount} written warnings`,
        language: letterLang,
      });
      setLetter(res.letter);
    } finally { setLoading(null); }
  };

  const genPIP = async () => {
    setLoading("pip");
    setPip(null);
    try {
      const res = await (api as any).aiCenter.pip({
        name: employee, role, issues: details, target_kpi: 78, actual_kpi: 60,
      });
      setPip(res);
    } finally { setLoading(null); }
  };

  return (
    <div>
      {/* Form */}
      <div style={card}>
        <div style={sectionTitle}>⚖️ Discipline Consultation <AIBadge /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
          <div>
            <label style={label}>Employee</label>
            <select style={selectStyle} value={employee} onChange={e => setEmployee(e.target.value)}>
              {EMPLOYEE_OPTIONS.map(e => <option key={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Issue Type</label>
            <select style={selectStyle} value={issueType} onChange={e => setIssueType(e.target.value)}>
              {ISSUE_TYPES.map(i => <option key={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Prior Verbal Warnings</label>
            <input style={inputStyle} type="number" min={0} value={warnCount}
                   onChange={e => setWarnCount(+e.target.value)} />
          </div>
          <div>
            <label style={label}>Prior Written Warnings</label>
            <input style={inputStyle} type="number" min={0} value={writtenCount}
                   onChange={e => setWrittenCount(+e.target.value)} />
          </div>
          <div>
            <label style={label}>Incident Date</label>
            <input style={inputStyle} type="date" value={incidentDate}
                   onChange={e => setIncidentDate(e.target.value)} />
          </div>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label style={label}>Issue Details</label>
          <AITextHelper
            value={details}
            onChange={e => setDetails(e.target.value)}
            placeholder="Describe the issue in detail…"
            context="HR discipline incident report"
            tone="professional"
            rows={3}
          />
        </div>
        <button style={btnPrimary} onClick={consult} disabled={!details.trim() || loading === "consult"}>
          {loading === "consult" ? "Consulting AI…" : "🤖 Get AI Consultation"}
        </button>
      </div>

      {/* Consultation result */}
      {consultation && (
        <div style={{ ...card, borderColor: "rgba(255,214,165,0.3)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.75rem" }}>
            <strong>AI Recommendation</strong>
            <AIBadge />
            <span style={{
              padding: "0.15rem 0.5rem", borderRadius: 8, fontSize: "0.75rem", fontWeight: 700,
              background: consultation.risk_level === "high" ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)",
              color: consultation.risk_level === "high" ? "#ef4444" : "#f59e0b",
            }}>
              {consultation.risk_level} risk
            </span>
          </div>
          <p style={{ margin: "0 0 0.5rem", fontWeight: 600, color: "#ffd6a5" }}>
            Action: {consultation.recommendation?.replace(/_/g, " ")}
          </p>
          <p style={{ margin: "0 0 0.5rem", fontSize: "0.875rem" }}>{consultation.legal_basis}</p>
          {consultation.next_steps?.map((s: string, i: number) => (
            <p key={i} style={{ margin: "0.25rem 0", fontSize: "0.85rem", color: "#94a3b8" }}>
              {i + 1}. {s}
            </p>
          ))}
          {consultation.caution && (
            <p style={{ margin: "0.75rem 0 0", fontSize: "0.8rem", color: "#f59e0b",
                        background: "rgba(245,158,11,0.1)", padding: "0.5rem", borderRadius: 6 }}>
              ⚠️ {consultation.caution}
            </p>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: "1rem", flexWrap: "wrap" }}>
            <select style={{ ...selectStyle, width: "auto" }} value={letterLang}
                    onChange={e => setLetterLang(e.target.value as "english" | "arabic")}>
              <option value="english">English</option>
              <option value="arabic">Arabic</option>
            </select>
            <button style={btnPrimary} onClick={genLetter} disabled={loading === "letter"}>
              {loading === "letter" ? "Generating…" : "📝 Generate Warning Letter"}
            </button>
            <button style={btnGhost} onClick={genPIP} disabled={loading === "pip"}>
              {loading === "pip" ? "Generating…" : "📋 Generate PIP Plan"}
            </button>
          </div>
        </div>
      )}

      {/* Warning letter */}
      {letter && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
            <strong>Warning Letter ({letterLang})</strong>
            <div style={{ display: "flex", gap: 8 }}>
              <CopyBtn text={letter} />
              <button style={btnGhost} onClick={() => window.print()}>🖨️ Print</button>
            </div>
          </div>
          <pre style={{
            whiteSpace: "pre-wrap", fontFamily: "Georgia, serif", fontSize: "0.875rem",
            lineHeight: 1.7, color: "#e2e8f0", background: "rgba(0,0,0,0.2)",
            padding: "1rem", borderRadius: 6,
          }}>
            {letter}
          </pre>
        </div>
      )}

      {/* PIP Plan */}
      {pip && !pip.error && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
            <strong>30-Day PIP Plan</strong>
            <div style={{ display: "flex", gap: 8 }}>
              <CopyBtn text={JSON.stringify(pip, null, 2)} />
              <button style={btnGhost} onClick={() => window.print()}>🖨️ Print</button>
            </div>
          </div>
          {pip.summary && <p style={{ marginBottom: "0.75rem", color: "#cbd5e1" }}>{pip.summary}</p>}
          {["week1","week2","week3","week4"].map(wk => pip[wk] && (
            <div key={wk} style={{ marginBottom: "0.75rem" }}>
              <strong style={{ color: "#ffd6a5" }}>{wk.replace("week","Week ")}</strong>
              <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>{pip[wk].goal}</p>
              {pip[wk].actions?.map((a: string, i: number) => (
                <p key={i} style={{ margin: "0.15rem 0 0 1rem", fontSize: "0.8rem", color: "#94a3b8" }}>• {a}</p>
              ))}
            </div>
          ))}
          {pip.consequences && (
            <p style={{ fontSize: "0.85rem", color: "#f87171", marginTop: "0.5rem" }}>
              ⚠️ Consequences: {pip.consequences}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 4 — EDUCATION HUB
// ══════════════════════════════════════════════════════════════════════════════

function TabEducation() {
  const [tutModule, setTutModule] = useState("crm");
  const [tutRole, setTutRole] = useState("employee");
  const [tutLang, setTutLang] = useState("english");
  const [tutorial, setTutorial] = useState<any>(null);

  const [brand, setBrand] = useState("enzo");
  const [audience, setAudience] = useState("distributor");
  const [guide, setGuide] = useState<any>(null);

  const [coachEmployee, setCoachEmployee] = useState(EMPLOYEE_OPTIONS[0]);
  const [coachGap, setCoachGap] = useState(10);
  const [coachPattern, setCoachPattern] = useState("late on Sundays");
  const [tip, setTip] = useState<string | null>(null);

  const [loading, setLoading] = useState<string | null>(null);

  const genTutorial = async () => {
    setLoading("tutorial"); setTutorial(null);
    try { setTutorial(await (api as any).aiCenter.tutorial(tutModule, tutRole, tutLang)); }
    finally { setLoading(null); }
  };

  const genGuide = async () => {
    setLoading("guide"); setGuide(null);
    try { setGuide(await (api as any).aiCenter.productGuide(brand, audience)); }
    finally { setLoading(null); }
  };

  const genTip = async () => {
    setLoading("tip"); setTip(null);
    try {
      const res = await (api as any).aiCenter.coachingTip({
        name: coachEmployee, role: "employee", gap: coachGap, pattern: coachPattern,
      });
      setTip(res.tip);
    } finally { setLoading(null); }
  };

  const TRAINING_STEPS = [
    {
      step: 1,
      icon: "📡",
      title: "AI Feed — Read your daily alerts",
      what: "The AI scans inventory, WhatsApp, CRM, pricing, and attendance every few hours and creates prioritised notifications.",
      how: "Go to the AI Feed tab. Use the filter buttons (Inventory / CRM / WhatsApp …) to focus on one area. Click Apply to act on the suggestion, or Dismiss to clear it.",
      tip: "Critical (red) alerts mean action is needed today. Low (green) are background suggestions.",
    },
    {
      step: 2,
      icon: "⚙️",
      title: "Workflows — Approve AI-suggested actions",
      what: "When the AI wants to send a WhatsApp, update a price, or follow up on a lead it creates a Workflow and waits for your approval.",
      how: "Open the Workflows tab → Pending. Read the recommendation. Click ✅ Approve to execute it or ❌ Reject to cancel. Check History to see what was done.",
      tip: "Never approve a workflow you don't understand — read the context block first.",
    },
    {
      step: 3,
      icon: "⚖️",
      title: "Discipline — Issue formal HR letters",
      what: "Generate professional disciplinary letters (warning, final warning, termination) with a single click.",
      how: "Open Discipline tab. Select the employee, choose the issue type (Attendance / Conduct …), describe the incident, pick severity, then Generate Letter. Copy or print the result.",
      tip: "Always get manager sign-off before printing a Final Warning or Termination letter.",
    },
    {
      step: 4,
      icon: "📖",
      title: "Module Tutorial — Train staff on any system",
      what: "Ask the AI to write a step-by-step tutorial for any AFAQ module in English or Arabic, tailored to employee / manager / admin role.",
      how: "Scroll down to Module Tutorial. Pick a module (e.g. inventory), pick the role and language, click Generate Tutorial. Share the result with the new staff member.",
      tip: "Arabic tutorials are useful for warehouse staff. English for management.",
    },
    {
      step: 5,
      icon: "🏷️",
      title: "Product Guide — Sales scripts for ENZO & LIZZE",
      what: "Generate a brand knowledge guide for distributors, retailers, salons, or end customers.",
      how: "Choose ENZO Italy or LIZZE Professional, pick your audience, click Generate Guide, then print or copy.",
      tip: "Use the 🖨️ print button to create a clean PDF for trade show handouts.",
    },
    {
      step: 6,
      icon: "💡",
      title: "Coaching Tip — Help underperforming staff",
      what: "Give the AI an employee's KPI gap and attendance pattern and it writes a specific, actionable coaching tip.",
      how: "Select the employee, enter the KPI gap (e.g. 15%), describe the pattern (e.g. 'late on Sundays'), click Get Coaching Tip. Use the tip in your next 1-on-1.",
      tip: "Be specific with the pattern — 'arrives 20 min late on Sundays' gives a better tip than 'late sometimes'.",
    },
    {
      step: 7,
      icon: "🔧",
      title: "Settings — Choose which AI engine runs what",
      what: "Control whether OpenAI (gpt-4o-mini) or local Ollama handles user-facing vs bulk tasks.",
      how: "Open Settings tab. Check which provider is configured (green dot = active). Never route customer-facing content through Ollama.",
      tip: "If OpenAI key is missing, all user-facing AI will fall back to DeepSeek — still works but slower.",
    },
  ];

  return (
    <div>
      {/* ── Static step-by-step training guide ── */}
      <div style={{ ...card, marginBottom: "2rem", borderColor: "rgba(255,214,165,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem" }}>
          <span style={{ fontSize: "1.4rem" }}>🎓</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: "1rem", color: "#ffd6a5" }}>How to use the AFAQ AI Brain</div>
            <div style={{ fontSize: "0.78rem", color: "#64748b" }}>Step-by-step guide for every tab — no AI required, always available</div>
          </div>
        </div>
        {/* M2 fix (audit): training steps grid 280px clips on narrow phones. */}
        <div className={grids["responsive-grid"]} style={{ gap: "0.75rem" }}>
          {TRAINING_STEPS.map(s => (
            <div key={s.step} style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 8,
              padding: "0.875rem",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{
                  background: "rgba(255,214,165,0.15)",
                  border: "1px solid rgba(255,214,165,0.3)",
                  borderRadius: 6,
                  padding: "0.15rem 0.5rem",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#ffd6a5",
                }}>
                  STEP {s.step}
                </span>
                <span style={{ fontSize: "1rem" }}>{s.icon}</span>
                <strong style={{ fontSize: "0.85rem", color: "#e2e8f0" }}>{s.title}</strong>
              </div>
              <p style={{ margin: "0 0 0.4rem", fontSize: "0.8rem", color: "#94a3b8" }}>{s.what}</p>
              <p style={{ margin: "0 0 0.4rem", fontSize: "0.8rem", color: "#cbd5e1" }}>
                <span style={{ color: "#ffd6a5", fontWeight: 600 }}>How: </span>{s.how}
              </p>
              <p style={{ margin: 0, fontSize: "0.75rem", color: "#ffd6a5", fontStyle: "italic" }}>
                💡 {s.tip}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: "1.5rem" }} />

      <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "#e2e8f0", marginBottom: "1rem" }}>
        🤖 AI Content Generators
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
      {/* Tutorial generator */}
      <div style={card}>
        <div style={sectionTitle}>📖 Module Tutorial <AIBadge /></div>
        <div style={{ display: "grid", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <div>
            <label style={label}>Module</label>
            <select style={selectStyle} value={tutModule} onChange={e => setTutModule(e.target.value)}>
              {MODULE_OPTIONS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Role</label>
            <select style={selectStyle} value={tutRole} onChange={e => setTutRole(e.target.value)}>
              {["employee","manager","admin"].map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Language</label>
            <select style={selectStyle} value={tutLang} onChange={e => setTutLang(e.target.value)}>
              <option value="english">English</option>
              <option value="arabic">Arabic</option>
            </select>
          </div>
        </div>
        <button style={btnPrimary} onClick={genTutorial} disabled={loading === "tutorial"}>
          {loading === "tutorial" ? "Generating…" : "Generate Tutorial"}
        </button>
        {tutorial && !tutorial.error && (
          <div style={{ marginTop: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong>{tutorial.title}</strong>
              <CopyBtn text={JSON.stringify(tutorial, null, 2)} />
            </div>
            <p style={{ fontSize: "0.78rem", color: "#64748b", margin: "0.25rem 0 0.75rem" }}>
              ~{tutorial.estimated_time}
            </p>
            {tutorial.steps?.map((s: any) => (
              <div key={s.step} style={{ marginBottom: "0.5rem" }}>
                <strong style={{ fontSize: "0.85rem" }}>Step {s.step}: {s.title}</strong>
                <p style={{ margin: "0.1rem 0", fontSize: "0.8rem", color: "#94a3b8" }}>{s.description}</p>
                {s.tip && (
                  <p style={{ margin: "0.1rem 0 0", fontSize: "0.78rem", color: "#ffd6a5" }}>
                    💡 {s.tip}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product guide */}
      <div style={card}>
        <div style={sectionTitle}>🏷️ Product Guide <AIBadge /></div>
        <div style={{ display: "grid", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <div>
            <label style={label}>Brand</label>
            <select style={selectStyle} value={brand} onChange={e => setBrand(e.target.value)}>
              <option value="enzo">ENZO Italy</option>
              <option value="lizze">LIZZE Professional</option>
            </select>
          </div>
          <div>
            <label style={label}>Audience</label>
            <select style={selectStyle} value={audience} onChange={e => setAudience(e.target.value)}>
              {["distributor","retailer","salon","end-customer"].map(a => <option key={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <button style={btnPrimary} onClick={genGuide} disabled={loading === "guide"}>
          {loading === "guide" ? "Generating…" : "Generate Guide"}
        </button>
        {guide && (
          <div style={{ marginTop: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <strong>Guide ready</strong>
              <div style={{ display: "flex", gap: 6 }}>
                <CopyBtn text={JSON.stringify(guide, null, 2)} />
                <button style={btnGhost} onClick={() => window.print()}>🖨️</button>
              </div>
            </div>
            <pre style={{
              whiteSpace: "pre-wrap", fontSize: "0.78rem", color: "#cbd5e1",
              background: "rgba(0,0,0,0.2)", padding: "0.75rem", borderRadius: 6,
              maxHeight: 300, overflow: "auto",
            }}>
              {JSON.stringify(guide, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Coaching tips — full width */}
      <div style={{ ...card, gridColumn: "1 / -1" }}>
        <div style={sectionTitle}>💡 Employee Coaching Tip <AIBadge /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
          <div>
            <label style={label}>Employee</label>
            <select style={selectStyle} value={coachEmployee} onChange={e => setCoachEmployee(e.target.value)}>
              {EMPLOYEE_OPTIONS.map(e => <option key={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>KPI Gap (%)</label>
            <input style={inputStyle} type="number" min={1} max={100} value={coachGap}
                   onChange={e => setCoachGap(+e.target.value)} />
          </div>
          <div>
            <label style={label}>Missed Pattern</label>
            <input style={inputStyle} value={coachPattern}
                   onChange={e => setCoachPattern(e.target.value)}
                   placeholder="e.g. late on Sundays" />
          </div>
        </div>
        <button style={btnPrimary} onClick={genTip} disabled={loading === "tip"}>
          {loading === "tip" ? "Thinking…" : "Get Coaching Tip"}
        </button>
        {tip && (
          <div style={{ ...card, marginTop: "0.75rem", marginBottom: 0, borderColor: "rgba(255,214,165,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong style={{ fontSize: "0.875rem" }}>Tip for {coachEmployee}</strong>
              <CopyBtn text={tip} />
            </div>
            <p style={{ margin: "0.5rem 0 0", fontSize: "0.9rem", lineHeight: 1.6 }}>{tip}</p>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 5 — SETTINGS
// ══════════════════════════════════════════════════════════════════════════════

function TabSettings() {
  const [settings, setSettings] = useState<AISettings | null>(null);
  const [testResult, setTestResult] = useState<Record<string, any> | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  useEffect(() => {
    (api as any).aiCenter.settings().then(setSettings).catch(() => {});
  }, []);

  const testProvider = async (provider: string) => {
    setTesting(provider); setTestResult(null);
    try { setTestResult(await (api as any).aiCenter.testProvider(provider)); }
    finally { setTesting(null); }
  };

  if (!settings) return <Thinking />;

  return (
    <div>
      {/* Provider status */}
      <div style={card}>
        <div style={sectionTitle}>🔌 Provider Status</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          {/* OpenAI */}
          <div style={{ ...card, margin: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>OpenAI gpt-4o-mini</strong>
              <span style={{
                fontSize: "0.75rem", fontWeight: 700, padding: "0.15rem 0.5rem",
                borderRadius: 8,
                background: settings.providers.openai.configured ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
                color: settings.providers.openai.configured ? "#22c55e" : "#ef4444",
              }}>
                {settings.providers.openai.configured ? "✅ Connected" : "❌ No API key"}
              </span>
            </div>
            <p style={{ margin: "0.5rem 0 0.75rem", fontSize: "0.8rem", color: "#64748b" }}>
              Primary — all user-facing tasks
              {settings.providers.openai.key_preview && (
                <span> · Key: <code style={{ color: "#94a3b8" }}>{settings.providers.openai.key_preview}</code></span>
              )}
            </p>
            <button style={btnGhost} onClick={() => testProvider("openai")} disabled={testing === "openai"}>
              {testing === "openai" ? "Testing…" : "Test OpenAI"}
            </button>
          </div>

          {/* Ollama */}
          <div style={{ ...card, margin: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>Ollama (local)</strong>
            </div>
            <p style={{ margin: "0.5rem 0 0.25rem", fontSize: "0.8rem", color: "#64748b" }}>
              Bulk tasks only · Veridian&#174;
            </p>
            <p style={{ margin: "0 0 0.75rem", fontSize: "0.78rem", color: "#475569" }}>
              {settings.providers.ollama.url}
            </p>
            <button style={btnGhost} onClick={() => testProvider("ollama")} disabled={testing === "ollama"}>
              {testing === "ollama" ? "Testing…" : "Test Ollama"}
            </button>
          </div>
        </div>

        {testResult && (
          <div style={{ ...card, marginTop: "1rem", marginBottom: 0 }}>
            <strong style={{ fontSize: "0.875rem" }}>
              {testResult.ok ? "✅" : "❌"} {testResult.provider} — {testResult.latency_ms}ms
            </strong>
            {testResult.response && (
              <p style={{ margin: "0.25rem 0 0", fontSize: "0.85rem", color: "#94a3b8" }}>
                "{testResult.response}"
              </p>
            )}
          </div>
        )}
      </div>

      {/* Feature toggles */}
      <div style={card}>
        <div style={sectionTitle}>⚙️ Features</div>
        {Object.entries(settings.features).map(([key, val]) => (
          <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                                   padding: "0.5rem 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <span style={{ fontSize: "0.9rem" }}>{key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
            <span style={{
              padding: "0.15rem 0.6rem", borderRadius: 10, fontSize: "0.75rem", fontWeight: 700,
              background: val ? "rgba(34,197,94,0.15)" : "rgba(100,116,139,0.2)",
              color: val ? "#22c55e" : "#64748b",
            }}>
              {val ? "ON" : "OFF"}
            </span>
          </div>
        ))}
      </div>

      {/* Provider hierarchy info */}
      <div style={card}>
        <div style={sectionTitle}>📋 Provider Rules</div>
        <div style={{ fontSize: "0.82rem", lineHeight: 1.8, color: "#94a3b8" }}>
          <strong style={{ color: "#ffd6a5" }}>OpenAI gpt-4o-mini</strong> →
          Notifications, discipline letters, workflows, tutorials, coaching tips, chat<br />
          <strong style={{ color: "#b8d58d" }}>Ollama local</strong> →
          Bulk processing, background jobs, product guide batch generation<br />
          <span style={{ color: "#ef4444" }}>⛔ Never Ollama for:</span>{" "}
          customer-facing messages, warning letters, legal/HR content
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

const AICommandCenter: React.FC = () => {
  const [activeTab, setActiveTab] = useState("feed");

  return (
    <div style={{ padding: "2rem", minHeight: "100vh", background: "#0b1326" }}>
      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ fontSize: "2rem", fontWeight: 800, marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: 12 }}>
          🤖 AFAQ AI Brain <AIBadge />
        </h1>
        <p style={{ color: "var(--text-muted, #9ca3af)", maxWidth: 680 }}>
          Your company's AI operations hub — real-time alerts from every module, approve or reject AI-suggested actions,
          issue HR discipline letters, generate employee tutorials, and control which AI model handles what.
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: "flex", gap: 4, marginBottom: "2rem", flexWrap: "wrap",
        borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: "0.5rem",
      }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: "0.5rem 1rem",
              background: activeTab === t.key ? "rgba(255,214,165,0.15)" : "transparent",
              border: "1px solid",
              borderColor: activeTab === t.key ? "rgba(255,214,165,0.4)" : "transparent",
              borderRadius: "6px 6px 0 0",
              color: activeTab === t.key ? "#ffd6a5" : "var(--text-muted, #9ca3af)",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: activeTab === t.key ? 700 : 400,
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "feed"       && <TabFeed />}
      {activeTab === "workflows"  && <TabWorkflows />}
      {activeTab === "discipline" && <TabDiscipline />}
      {activeTab === "education"  && <TabEducation />}
      {activeTab === "settings"   && <TabSettings />}

      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        @media print {
          .afaq-sidebar, nav, button { display: none !important; }
          body { background: white; color: black; }
          pre { border: 1px solid #ccc; }
        }
      `}</style>
    </div>
  );
};

export default AICommandCenter;
